import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { ChallengeResponse } from "@src/pages/super_task_manager/questions/CategoryQuestions.types";

const BASE_URL: string = import.meta.env.VITE_TEST_FRESHERS_CHALLENGE_BASE_URL;

export interface FilterPayload {
  listIds?: string[];
  difficultyLevel?: string[];
  topicIds?: string[];
}

export interface OverallPerformance {
  totalQuestions: number;
  totalHardQuestions: number;
  totalMediumQuestions: number;
  totalEasyQuestions: number;
  attendedQuestion: number;
  attendedHardQuestions: number;
  attendedMediumQuestions: number;
  attendedEasyQuestions: number;
}

export interface OverallPerformanceResponse {
  statusCode: number;
  httpStatus: string;
  response: OverallPerformance;
}

export const CategoryQuestionsApiSlice = createApi({
  reducerPath: "categoryQuestionsApi",
  baseQuery: fetchBaseQuery({ baseUrl: BASE_URL }),
  tagTypes: ["getCategoryQuestions", "getCategorryQuestionsById"],
  endpoints: (builder) => ({
    getCategoryQuestions: builder.query<
      any,
      { categoryId?: string; pageNo: number; pageSize: number }
    >({
      query: ({ categoryId, pageNo, pageSize }) => ({
        url: `challenge/get-all?categoryId=${categoryId}&pageNo=${pageNo}&pageSize=${pageSize}`,
        method: "GET",
      }),
      providesTags: ["getCategoryQuestions"],
    }),
    getCategoryQuestionsForStudent: builder.mutation<
      any,
      {
        categoryId: string;
        pageNo: number;
        pageSize: number;
        userId?: string;
        payload?: FilterPayload | {};
      }
    >({
      query: ({ payload, categoryId, pageNo, pageSize, userId }) => {
        const params = new URLSearchParams({
          categoryId,
          pageNo: String(pageNo),
          pageSize: String(pageSize),
        });

        if (userId) {
          params.append("userId", userId);
        }

        return {
          url: `challenge/get-by-student?${params.toString()}`,
          method: "POST",
          body: payload,
        };
      },
    }),
    getCategoryQuestionsById: builder.query<
      { httpStatus: string; statusCode: number; response: ChallengeResponse },
      {
        challengeId: number;
      }
    >({
      query: ({ challengeId }) => ({
        url: `challenge/get-challenge-by-id?challengeId=${challengeId}`,
        method: "GET",
      }),
      providesTags: ["getCategorryQuestionsById"],
    }),
    getCategoryQuestionsSearch: builder.query<
      any,
      { keyword: string; categoryId: string }
    >({
      query: ({ keyword, categoryId }) => ({
        url: `challenge/search-question?keyword=${keyword}&categoryId=${categoryId}`,
        method: "GET",
      }),
    }),
    getChallengeQuesSummary: builder.query<
      any,
      { questionID: string; userId?: string }
    >({
      query: ({ questionID, userId }) => ({
        url: `challenge/editor/${questionID}${
          userId ? `?userId=${userId}` : ""
        }`,
        method: "GET",
      }),
    }),
    getOverallPerformance: builder.query<
      OverallPerformanceResponse,
      { userID: string; categoryId: string }
    >({
      query: ({ userID, categoryId }) => ({
        url: `challenge/get-overall-performance?userId=${userID}&categoryId=${categoryId}`,
        method: "GET",
      }),
    }),
    getUserChallengeSubmission: builder.query<
      any,
      { challengeId: number; userId: string }
    >({
      query: ({ challengeId, userId }) => ({
        url: `challenge-submission/get-challenge-submissions?challengeId=${challengeId}&userId=${userId}`,
        method: "GET",
      }),
    }),
    addRating: builder.mutation<
      any,
      { challengeId: number; userId: string; rating: number }
    >({
      query: ({ challengeId, userId, rating }) => ({
        url: `challenge/post-challenge-rate?challengeId=${challengeId}&userId=${userId}&rating=${rating}`,
        method: "POST",
      }),
    }),
  }),
});

export const {
  useGetCategoryQuestionsQuery,
  useLazyGetCategoryQuestionsQuery,
  useGetCategoryQuestionsForStudentMutation,
  useLazyGetCategoryQuestionsSearchQuery,
  useGetCategoryQuestionsByIdQuery,
  useLazyGetCategoryQuestionsByIdQuery,
  useLazyGetChallengeQuesSummaryQuery,
  useGetOverallPerformanceQuery,
  useAddRatingMutation,
  useLazyGetUserChallengeSubmissionQuery,
} = CategoryQuestionsApiSlice;
